import React from 'react';
import { HiChevronUp, HiChevronDown } from 'react-icons/hi';
import styles from './Table.module.css';

export type SortDirection = 'asc' | 'desc' | null;

export interface TableHeaderProps {
  columns: string[];
  columnWidths?: string[];
  columnAlignments?: ('left' | 'center' | 'right')[];
  className?: string;
  sortableColumns?: string[];
  sortColumn?: string | null;
  sortDirection?: SortDirection;
  onSort?: (column: string) => void;
  columnKeys?: (string | null)[]; // Optional keys for each column (for sorting)
}

const TableHeader: React.FC<TableHeaderProps> = ({
  columns,
  columnWidths,
  columnAlignments,
  className = '',
  sortableColumns = [],
  sortColumn = null,
  sortDirection = null,
  onSort,
  columnKeys
}) => {
  const gridTemplateColumns = columnWidths 
    ? columnWidths.join(' ') 
    : `repeat(${columns.length}, 1fr)`;

  const getColumnKey = (index: number): string | null => {
    if (columnKeys && columnKeys[index]) {
      return columnKeys[index];
    }
    return null;
  };

  const handleSort = (columnKey: string) => {
    if (sortableColumns.includes(columnKey) && onSort) {
      onSort(columnKey);
    }
  };

  const getSortIcon = (columnKey: string | null) => {
    if (!columnKey || !sortableColumns.includes(columnKey)) return null;
    
    if (sortColumn === columnKey && sortDirection) {
      if (sortDirection === 'asc') {
        // Chevron up (ascending)
        return (
          <>
            {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
            {/* @ts-ignore - react-icons compatibility with React 19 */}
            <HiChevronUp size={12} className={styles.sortIcon} />
          </>
        );
      } else if (sortDirection === 'desc') {
        // Chevron down (descending)
        return (
          <>
            {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
            {/* @ts-ignore - react-icons compatibility with React 19 */}
            <HiChevronDown size={12} className={styles.sortIcon} />
          </>
        );
      }
    }
    // Default state - chevron up and down (unsorted)
    return (
      <div className={styles.sortIconUnsorted}>
        {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
        {/* @ts-ignore - react-icons compatibility with React 19 */}
        <HiChevronUp size={12} className={styles.sortIcon} />
        {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
        {/* @ts-ignore - react-icons compatibility with React 19 */}
        <HiChevronDown size={12} className={styles.sortIcon} />
      </div>
    );
  };

  return (
    <div 
      className={`${styles.tableHeader} ${className}`}
      style={{ gridTemplateColumns }}
    >
      {columns.map((column, index) => {
        const columnKey = getColumnKey(index);
        const isSortable = columnKey !== null && sortableColumns.includes(columnKey);
        const isActive = sortColumn === columnKey && sortDirection !== null;
        
        return (
          <div 
            key={index} 
            className={`${styles.headerCell} ${isSortable ? styles.headerCellSortable : ''} ${isActive ? styles.headerCellActive : ''}`}
            style={columnAlignments?.[index] === 'right' ? { justifyContent: 'flex-end' } : columnAlignments?.[index] === 'center' ? { justifyContent: 'center' } : undefined}
            onClick={() => isSortable && columnKey && handleSort(columnKey)}
          >
            <span>{column}</span>
            {isSortable && (
              <span className={styles.sortIconContainer}>
                {getSortIcon(columnKey)}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default TableHeader;

